import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { Star } from 'lucide-react';

const REVIEWS = [
  { user: 'Maximilian S.', location: 'Cologne', stars: 5, text: 'V2 jumped my FPS from 110 to 220. My teammates noticed before I even told them.' },
  { user: 'Jonas K.', location: 'Berlin', stars: 5, text: 'Installed in 5 minutes, ran a benchmark right after — results don\'t lie. Worth every cent.' },
  { user: 'Luca M.', location: 'Munich', stars: 5, text: 'Tested for 3 months straight. Zero issues, massive improvement. V2 is a no-brainer.' },
  { user: 'Felix B.', location: 'Hamburg', stars: 5, text: 'The macro suite in V2 alone is worth the price. My setup feels like a different PC.' },
  { user: 'Leon W.', location: 'Vienna', stars: 5, text: 'Solid product. No bloatware, no bs. Just tweaks that actually work.' },
  { user: 'Noah P.', location: 'Frankfurt', stars: 5, text: 'Input lag cut from 26ms to 8ms. In competitive gaming, that\'s the difference between win and lose.' },
  { user: 'Elias R.', location: 'Stuttgart', stars: 5, text: 'Support on Discord is fast and actually helpful. Rare to find these days.' },
  { user: 'Paul H.', location: 'Düsseldorf', stars: 5, text: 'I was skeptical at first. Now I can\'t imagine gaming without V2. Simple as that.' },
];

function Stars({ count }) {
  return (
    <div className="flex gap-0.5">
      {Array.from({ length: count }).map((_, i) => (
        <Star key={i} size={10} className="text-white fill-white" />
      ))}
    </div>
  );
}

function ReviewCard({ review }) {
  return (
    <div
      className="flex-shrink-0 w-72 flex flex-col items-start p-6 rounded-2xl border border-white/10 backdrop-blur-xl mx-3"
      style={{ background: 'rgba(255,255,255,0.025)' }}
    >
      <div className="flex items-center justify-between w-full mb-3">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-white/10 border border-white/15 flex items-center justify-center flex-shrink-0">
            <span className="text-white text-xs font-medium">{review.user[0]}</span>
          </div>
          <div>
            <span className="font-outfit text-sm font-medium text-white block leading-tight">{review.user}</span>
            <span className="font-inter text-xs text-zinc-700">{review.location}</span>
          </div>
        </div>
        <Stars count={review.stars} />
      </div>
      <p className="font-inter text-sm text-zinc-500 leading-relaxed">{review.text}</p>
    </div>
  );
}

export default function ReviewsSection() {
  const { lang } = useLanguage();
  const doubled = [...REVIEWS, ...REVIEWS];

  return (
    <section id="reviews" className="py-40 relative overflow-hidden">
      <motion.div initial={{ scaleY: 0 }} whileInView={{ scaleY: 1 }} transition={{ duration: 0.8 }}
        viewport={{ once: true }}
        className="absolute top-0 left-1/2 -translate-x-1/2 w-px h-24 bg-gradient-to-b from-white/25 to-transparent origin-top"
      />
      <motion.div animate={{ x: [-20, 20, -20], y: [-10, 10, -10] }} transition={{ duration: 10, repeat: Infinity }}
        className="absolute top-1/3 left-1/6 w-80 h-80 rounded-full pointer-events-none opacity-[0.04]"
        style={{ background: 'radial-gradient(circle, white 0%, transparent 70%)', filter: 'blur(50px)' }}
      />

      {/* Heading */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center mb-14 relative z-10">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }} viewport={{ once: true }}
        >
          <p className="font-inter text-xs tracking-[0.3em] text-zinc-700 uppercase mb-4">Testimonials</p>
          <div className="inline-flex items-center gap-3 px-5 py-2.5 rounded-full border border-white/10 bg-white/[0.03] backdrop-blur-xl mb-6">
            <div className="flex gap-0.5">
              {[...Array(5)].map((_, i) => <Star key={i} size={10} className="text-white fill-white" />)}
            </div>
            <span className="font-outfit text-2xl font-light text-white">70+</span>
            <span className="font-inter text-sm text-zinc-500">
              {lang === 'de' ? 'zufriedene Nutzer' : 'satisfied users'}
            </span>
          </div>
          <h2 className="font-outfit text-4xl sm:text-5xl font-light tracking-tight text-white mb-3">
            {lang === 'de' ? 'Was unsere Community sagt' : 'What our community says'}
          </h2>
          <p className="font-inter text-zinc-500 text-base">
            {lang === 'de' ? 'Echte Nutzer, echte Ergebnisse.' : 'Real users, real results.'}
          </p>
        </motion.div>
      </div>

      {/* Infinite carousel */}
      <div className="relative overflow-hidden">
        {/* Left fade */}
        <div className="absolute left-0 top-0 bottom-0 w-24 z-10 pointer-events-none"
          style={{ background: 'linear-gradient(to right, #000, transparent)' }} />
        {/* Right fade */}
        <div className="absolute right-0 top-0 bottom-0 w-24 z-10 pointer-events-none"
          style={{ background: 'linear-gradient(to left, #000, transparent)' }} />

        <div className="carousel-track py-4" data-testid="reviews-carousel">
          {doubled.map((r, i) => (
            <ReviewCard key={i} review={r} />
          ))}
        </div>
      </div>
    </section>
  );
}
